import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { checkHasRouteAccess } from 'src/hooks/useModulePermissions';
import { GetCompanyList } from 'src/graphql/dashboard';
import { isCobranzaOnlyRestrictedContext, COBRANZA_ONLY_ROUTE } from 'src/utils/cobranzaOnlyUser';
import { Roles } from 'src/utils/roles';

/** Misma regla que el sidebar: acceso al módulo y proceso aún no cerrado. */
function isSoftlandingSidebarVisible(company, accessContext) {
  if (company?.softlanding?.isCompleted !== false) return false;
  return checkHasRouteAccess('/softlanding', accessContext);
}

const LANDING_CANDIDATES = [
  '/dashboard',
  '/reportes_ventas',
  '/reportes_compras',
  '/reportes_cobranza',
  '/reportes_proveedores',
  '/reportes_dj',
  '/reportes_personalizados',
  '/reportes_rrhh',
  '/flujo_caja',
  '/eerr',
  '/eoaf',
  '/creditos',
  '/documentos_tributarios',
  '/boletas',
  '/obligaciones',
  '/conciliacion',
  '/cobranza',
  '/pago_proveedores',
  '/cuadre_cartolas',
  '/formulario-29',
  '/consolidado-f29',
  '/renta',
  '/estado_dj',
  '/asientos',
  '/plan_de_cuentas',
  '/centralizacion_contable',
  '/libro-compra-venta',
  '/repositorio',
  '/conexiones',
  '/agenda',
  '/configuracion',
];

/**
 * Post-login landing redirect. Waits for user permissions AND fresh company
 * permissions (companiesByUser) before picking the first accessible module.
 */
const DefaultLandingRedirect = () => {
  const history = useHistory();
  const {
    roles,
    uid,
    modulePermissionsLoaded,
    modulePermissions: userModulePermissions,
  } = useSelector((state) => state.auth);
  const { selectedCompany } = useSelector((state) => state.companyList);
  const { data, fetching, error } = GetCompanyList();
  const [waitedLongEnough, setWaitedLongEnough] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setWaitedLongEnough(true), 8000);
    return () => clearTimeout(t);
  }, []);

  // Use freshest company data from network (avoids stale Redux cache on login)
  const freshSelectedCompany = useMemo(() => {
    if (!selectedCompany?.id) return null;
    const list = data?.companiesByUser;
    if (!Array.isArray(list) || list.length === 0) return selectedCompany;
    return (
      list.find((c) => String(c.id) === String(selectedCompany.id)) || selectedCompany
    );
  }, [data?.companiesByUser, selectedCompany]);

  const companies = data?.companiesByUser;
  const querySettled = !fetching && (Array.isArray(companies) || Boolean(error));
  const emptyCompanyList = querySettled && Array.isArray(companies) && companies.length === 0;
  const ready =
    querySettled &&
    Boolean(freshSelectedCompany?.id) &&
    (modulePermissionsLoaded || waitedLongEnough);

  const target = useMemo(() => {
    if (!ready) return null;

    if (isCobranzaOnlyRestrictedContext(uid, freshSelectedCompany?.id)) {
      return COBRANZA_ONLY_ROUTE;
    }

    const ctx = {
      userModulePermissions: userModulePermissions || [],
      modulePermissionsLoaded,
      roles: roles || [],
      selectedCompany: freshSelectedCompany,
      currentUserId: uid,
    };

    const isUserSmartCfo = (roles || []).includes(Roles.USER_SMARTCFO);
    if (isUserSmartCfo && isSoftlandingSidebarVisible(freshSelectedCompany, ctx)) {
      return '/softlanding';
    }

    return (
      LANDING_CANDIDATES.find((path) => checkHasRouteAccess(path, ctx)) ||
      '/configuracion'
    );
  }, [ready, userModulePermissions, modulePermissionsLoaded, roles, freshSelectedCompany, uid]);

  useEffect(() => {
    if (!target) return;
    history.replace(target);
  }, [history, target]);

  if (emptyCompanyList) {
    return (
      <div className="pt-5 text-center">
        <p className="tw-text-sm tw-text-slate-600">
          No hay empresas asignadas a esta cuenta. Contacta a un administrador.
        </p>
      </div>
    );
  }

  if (querySettled && error && !freshSelectedCompany?.id) {
    return (
      <div className="pt-5 text-center">
        <p className="tw-text-sm tw-text-slate-600">
          No se pudo cargar el espacio de trabajo. Recarga la página.
        </p>
      </div>
    );
  }

  return (
    <div className="pt-3 text-center">
      <div className="sk-spinner sk-spinner-pulse" />
    </div>
  );
};

export default DefaultLandingRedirect;
